import React from 'react';
import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import { 
  Users,
  BarChart3,
  Package,
  Megaphone,
  FolderKanban,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Star,
  Play,
  Shield,
  Globe,
  Cloud,
  Database,
  Workflow,
  Brain,
  Target,
  Award,
  Clock,
  TrendingUp,
  Zap,
  Lock,
  HeadphonesIcon,
  Download
} from 'lucide-react';

const CustomSolution = () => {
  const { solutionName } = useParams();

  const customSolutionData = {
    aquacrm: {
      name: 'AquaCRM',
      tagline: 'Enterprise Customer Relationship Management',
      description: 'Enterprise customer relationship management with AI-powered insights and automation.',
      fullDescription: 'AquaCRM is a comprehensive customer relationship management platform that combines traditional CRM functionality with advanced AI capabilities. It provides a complete 360-degree view of your customers, automates sales processes, and delivers predictive insights to help you build stronger customer relationships and drive revenue growth.',
      icon: Users,
      color: 'from-blue-500 to-indigo-500',
      category: 'Customer Relationship Management',
      features: [
        '360° Customer View',
        'Sales Pipeline Management',
        'Marketing Automation',
        'Service Integration',
        'AI-Powered Lead Scoring',
        'Advanced Analytics & Reporting',
        'Mobile CRM Access',
        'Third-party Integrations',
        'Custom Workflows',
        'Real-time Collaboration',
        'Territory Management',
        'Forecasting & Predictions'
      ],
      benefits: [
        'Increase sales productivity by 40%',
        'Improve customer retention rates',
        'Accelerate deal closure times',
        'Better lead qualification',
        'Enhanced customer insights',
        'Streamlined sales processes'
      ],
      metrics: {
        productivity: '40% increase in sales productivity',
        retention: '25% better customer retention',
        conversion: '35% higher conversion rates',
        revenue: '20% revenue growth'
      },
      industries: ['Technology', 'Healthcare', 'Financial Services', 'Manufacturing', 'Retail', 'Real Estate'],
      pricing: {
        starter: '$49/user/month',
        professional: '$89/user/month',
        enterprise: 'Custom pricing'
      }
    },
    aquahr: {
      name: 'AquaHR',
      tagline: 'Complete Human Resources Management',
      description: 'Complete human resources management system with talent acquisition and performance tracking.',
      fullDescription: 'AquaHR transforms human resources management by providing a comprehensive platform for talent acquisition, employee management, performance tracking, and organizational development. From recruitment to retirement, AquaHR streamlines every aspect of the employee lifecycle while ensuring compliance and enhancing employee engagement.',
      icon: BarChart3,
      color: 'from-green-500 to-teal-500',
      category: 'Human Resources',
      features: [
        'Talent Management',
        'Performance Analytics',
        'Payroll Integration',
        'Compliance Tracking',
        'Employee Self-Service Portal',
        'Recruitment & Onboarding',
        'Time & Attendance Tracking',
        'Benefits Administration',
        'Learning & Development',
        'Employee Engagement Surveys',
        'Organizational Charts',
        'Document Management'
      ],
      benefits: [
        'Reduce HR administrative tasks by 60%',
        'Improve employee satisfaction scores',
        'Accelerate hiring processes',
        'Ensure regulatory compliance',
        'Better talent retention',
        'Data-driven HR decisions'
      ],
      metrics: {
        efficiency: '60% reduction in HR admin tasks',
        satisfaction: '30% higher employee satisfaction',
        retention: '25% better talent retention',
        compliance: '100% regulatory compliance'
      },
      industries: ['All Industries', 'Large Enterprises', 'Government', 'Healthcare', 'Education', 'Non-Profit'],
      pricing: {
        starter: '$8/employee/month',
        professional: '$15/employee/month',
        enterprise: 'Custom pricing'
      }
    },
    aquainventory: {
      name: 'AquaInventory',
      tagline: 'Advanced Inventory & Supply Chain Management',
      description: 'Advanced inventory and supply chain management with predictive analytics.',
      fullDescription: 'AquaInventory revolutionizes supply chain and inventory management with real-time tracking, predictive analytics, and intelligent automation. It helps businesses optimize inventory levels, reduce costs, improve supplier relationships, and ensure product quality throughout the supply chain.',
      icon: Package,
      color: 'from-orange-500 to-red-500',
      category: 'Supply Chain Management',
      features: [
        'Real-time Tracking',
        'Demand Forecasting',
        'Supplier Management',
        'Quality Control',
        'Multi-location Inventory',
        'Automated Reordering',
        'Barcode & RFID Support',
        'Cost Analysis & Optimization',
        'Supply Chain Visibility',
        'Warehouse Management',
        'Returns Management',
        'Integration with ERP Systems'
      ],
      benefits: [
        'Reduce inventory costs by 25%',
        'Improve order fulfillment rates',
        'Minimize stockouts and overstock',
        'Better supplier relationships',
        'Enhanced supply chain visibility',
        'Automated inventory processes'
      ],
      metrics: {
        cost: '25% reduction in inventory costs',
        accuracy: '99% inventory accuracy',
        fulfillment: '95% order fulfillment rate',
        efficiency: '40% process efficiency gain'
      },
      industries: ['Manufacturing', 'Retail', 'E-commerce', 'Distribution', 'Wholesale', 'Automotive'],
      pricing: {
        starter: '$199/month',
        professional: '$399/month',
        enterprise: 'Custom pricing'
      }
    },
    aquamarketing: {
      name: 'AquaMarketing',
      tagline: 'Comprehensive Marketing Automation Platform',
      description: 'Comprehensive marketing automation platform with multi-channel campaign management.',
      fullDescription: 'AquaMarketing empowers marketing teams with a complete suite of automation tools for creating, executing, and measuring marketing campaigns across multiple channels. From lead generation to customer nurturing, it provides everything needed to drive engagement, conversions, and revenue growth.',
      icon: Megaphone,
      color: 'from-purple-500 to-pink-500',
      category: 'Marketing Automation',
      features: [
        'Campaign Management',
        'Lead Scoring',
        'Social Media Integration',
        'ROI Analytics',
        'Email Marketing Automation',
        'Content Management',
        'A/B Testing Platform',
        'Customer Journey Mapping',
        'Multi-channel Attribution',
        'Personalization Engine',
        'Marketing Qualified Lead Tracking',
        'Integration with CRM & Sales Tools'
      ],
      benefits: [
        'Increase lead generation by 50%',
        'Improve campaign ROI',
        'Better lead nurturing processes',
        'Enhanced customer segmentation',
        'Automated marketing workflows',
        'Data-driven marketing decisions'
      ],
      metrics: {
        leads: '50% increase in qualified leads',
        roi: '3x marketing ROI improvement',
        conversion: '25% higher conversion rates',
        engagement: '40% better engagement rates'
      },
      industries: ['Technology', 'E-commerce', 'Healthcare', 'Financial Services', 'Education', 'SaaS'],
      pricing: {
        starter: '$149/month',
        professional: '$299/month',
        enterprise: 'Custom pricing'
      }
    },
    aquaproject: {
      name: 'AquaProject',
      tagline: 'Enterprise Project Management',
      description: 'Enterprise project management with resource optimization and collaboration tools.',
      fullDescription: 'AquaProject delivers comprehensive project management capabilities designed for complex enterprise environments. It combines traditional project management methodologies with modern collaboration tools, resource optimization algorithms, and real-time analytics to ensure project success and team productivity.',
      icon: FolderKanban,
      color: 'from-cyan-500 to-blue-500',
      category: 'Project Management',
      features: [
        'Resource Planning',
        'Gantt Charts',
        'Team Collaboration',
        'Budget Management',
        'Time Tracking & Billing',
        'Risk Management',
        'Portfolio Management',
        'Agile & Waterfall Support',
        'Custom Workflows',
        'Document Management',
        'Milestone Tracking',
        'Integration with Development Tools'
      ],
      benefits: [
        'Deliver projects 30% faster',
        'Improve resource utilization',
        'Better project visibility',
        'Enhanced team collaboration',
        'Reduced project risks',
        'Optimized budget management'
      ],
      metrics: {
        delivery: '30% faster project delivery',
        utilization: '80% resource utilization',
        success: '95% project success rate',
        collaboration: '40% better team collaboration'
      },
      industries: ['Technology', 'Construction', 'Consulting', 'Healthcare', 'Government', 'Manufacturing'],
      pricing: {
        starter: '$12/user/month',
        professional: '$24/user/month',
        enterprise: 'Custom pricing'
      }
    }
  };

  const solution = customSolutionData[solutionName?.toLowerCase()];

  if (!solution) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Solution Not Found</h1>
          <p className="text-xl text-gray-600 mb-8">The custom solution you're looking for doesn't exist.</p>
          <Link to="/products" className="btn-primary">
            ← Back to Products
          </Link>
        </div>
      </div>
    );
  }

  const IconComponent = solution.icon;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className={`relative bg-gradient-to-br ${solution.color} py-20 lg:py-32`}>
        <div className="absolute inset-0 bg-black/20"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Link 
                to="/products" 
                className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Products
              </Link>

              <div className="flex items-center justify-center mb-6">
                <div className="w-20 h-20 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <IconComponent className="h-10 w-10 text-white" />
                </div>
              </div>

              <div className="inline-flex items-center bg-white/10 rounded-full px-6 py-2 mb-6">
                <Star className="h-4 w-4 text-yellow-400 mr-2" />
                <span className="text-sm font-semibold">Custom Enterprise Solution</span>
              </div>

              <h1 className="text-5xl lg:text-7xl font-bold mb-6">
                {solution.name}
              </h1>
              
              <p className="text-2xl lg:text-3xl font-semibold mb-8 text-white/90">
                {solution.tagline}
              </p>
              
              <p className="text-xl lg:text-2xl text-white/80 mb-12 leading-relaxed max-w-3xl mx-auto">
                {solution.description}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
                >
                  Request Custom Quote
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-gray-900 transition-colors duration-300 flex items-center justify-center group"
                >
                  <Play className="mr-2 h-5 w-5" />
                  Schedule Demo
                </motion.button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Key Metrics */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {Object.entries(solution.metrics).map(([key, value], index) => (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-6 bg-gray-50 rounded-xl hover:shadow-lg transition-shadow"
              >
                <div className={`text-3xl lg:text-4xl font-bold bg-gradient-to-r ${solution.color} bg-clip-text text-transparent mb-2`}>
                  {value.split(' ')[0]}
                </div>
                <div className="text-sm text-gray-600 font-medium">{value.replace(value.split(' ')[0] + ' ', '')}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Solution Overview */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                  Why Choose {solution.name}?
                </h2>
                <p className="text-xl text-gray-600 leading-relaxed">
                  {solution.fullDescription}
                </p>
              </div>

              <div className="space-y-4">
                <h3 className="text-2xl font-bold text-gray-900">Key Benefits:</h3>
                <div className="grid grid-cols-1 gap-3">
                  {solution.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-center space-x-3">
                      <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 font-medium">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className={`bg-gradient-to-br ${solution.color} rounded-2xl p-8 text-white`}>
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h4 className="text-2xl font-bold">{solution.name} Dashboard</h4>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                      <span className="text-sm font-medium">Live Demo</span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/20 backdrop-blur-sm p-4 rounded-lg">
                      <div className="text-3xl font-bold mb-1">98.9%</div>
                      <div className="text-sm opacity-90">System Uptime</div>
                    </div>
                    
                    <div className="bg-white/20 backdrop-blur-sm p-4 rounded-lg">
                      <div className="text-3xl font-bold mb-1">24/7</div>
                      <div className="text-sm opacity-90">Enterprise Support</div>
                    </div>
                  </div>
                  
                  <div className="bg-white/20 backdrop-blur-sm p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-medium">Implementation Success</span>
                      <span className="text-green-300 font-bold">Excellent</span>
                    </div>
                    <div className="w-full bg-white/20 rounded-full h-3">
                      <div className="bg-green-400 h-3 rounded-full animate-pulse" style={{ width: '96%' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
            >
              Complete Feature Set
            </motion.h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need for {solution.category.toLowerCase()} in one comprehensive, customizable platform.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {solution.features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-gray-50 p-6 rounded-xl hover:shadow-lg hover:bg-white transition-all duration-300 group"
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${solution.color}`}></div>
                  <span className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                    {feature}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
            >
              Trusted Across Industries
            </motion.h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {solution.name} serves diverse industries with specialized configurations for each sector's unique requirements.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {solution.industries.map((industry, index) => (
              <motion.span
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`px-6 py-3 bg-gradient-to-r ${solution.color} text-white rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transition-shadow`}
              >
                {industry}
              </motion.span>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
            >
              Flexible Pricing Options
            </motion.h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose the plan that fits your organization's needs. All plans include setup assistance and ongoing support.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {Object.entries(solution.pricing).map(([plan, price], index) => (
              <motion.div
                key={plan}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 ${
                  plan === 'professional' ? `border-4 border-gradient-to-r ${solution.color}` : 'border border-gray-200'
                }`}
              >
                <div className="text-center space-y-6">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 capitalize mb-2">{plan}</h3>
                    <div className="text-4xl font-bold text-gray-900">
                      {price === 'Custom pricing' ? (
                        <span className="text-2xl">Custom</span>
                      ) : (
                        <>
                          <span className="text-lg text-gray-500">$</span>
                          {price.replace('$', '').split('/')[0]}
                          <span className="text-lg text-gray-500">/{price.includes('/user/') ? 'user/' : ''}month</span>
                        </>
                      )}
                    </div>
                  </div>

                  <button
                    className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-300 ${
                      plan === 'professional'
                        ? `bg-gradient-to-r ${solution.color} text-white hover:shadow-lg`
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                  >
                    {plan === 'enterprise' ? 'Contact Sales' : 'Request Quote'}
                  </button>

                  <div className="text-sm text-gray-600">
                    {plan === 'starter' && 'Perfect for small teams'}
                    {plan === 'professional' && 'Most popular choice'}
                    {plan === 'enterprise' && 'For large organizations'}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={`py-20 bg-gradient-to-r ${solution.color}`}>
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-white"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              Ready to Transform Your {solution.category}?
            </h2>
            <p className="text-xl mb-10 opacity-90">
              Join organizations worldwide who are already using {solution.name} to streamline operations and drive growth.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-gray-900 px-8 py-4 rounded-lg font-bold text-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
              >
                Request Custom Solution
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-gray-900 transition-colors duration-300 flex items-center justify-center group"
              >
                <HeadphonesIcon className="mr-2 h-5 w-5" />
                Talk to Expert
              </motion.button>
            </div>

            <div className="mt-8 text-white/80 text-sm">
              <p>✓ Free consultation  ✓ Custom implementation  ✓ Enterprise support included</p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CustomSolution; 